// CreditNova Off-Balance Sheet Dialog Component
// Allows users to add off-balance sheet items for custom ratio calculations
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileSpreadsheet, 
  Plus, 
  Trash2,
  Save
} from 'lucide-react';

// ===========================================
// Types
// ===========================================

interface OffBalanceSheetItem {
  id: string;
  name: string;
  value: string;
  description?: string;
}

interface OffBalanceSheetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: OffBalanceSheetItem[];
  onSave: (items: OffBalanceSheetItem[]) => void;
}

// ===========================================
// Common Off-Balance Sheet Items
// ===========================================

const suggestedItems = [
  { name: 'Operating Leases', description: 'Future minimum lease payments' },
  { name: 'Loan Guarantees', description: 'Contingent liabilities from guarantees' },
  { name: 'Letters of Credit', description: 'Outstanding letters of credit' },
  { name: 'Purchase Commitments', description: 'Contractual purchase obligations' },
  { name: 'Pension Obligations', description: 'Unfunded pension liabilities' },
  { name: 'Factored Receivables', description: 'Sold receivables with recourse' },
];

// ===========================================
// Helper Functions
// ===========================================

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// ===========================================
// ItemRow Component
// ===========================================

interface ItemRowProps {
  item: OffBalanceSheetItem;
  onChange: (item: OffBalanceSheetItem) => void;
  onDelete: () => void;
}

function ItemRow({ item, onChange, onDelete }: ItemRowProps) {
  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
      <div className="flex-1 grid grid-cols-2 gap-3">
        <Input
          placeholder="Item name"
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
        />
        <Input
          placeholder="Value (e.g., $500,000)"
          value={item.value}
          onChange={(e) => onChange({ ...item, value: e.target.value })}
        />
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onDelete}
        className="text-muted-foreground hover:text-destructive shrink-0"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

// ===========================================
// OffBalanceSheetDialog Main Component
// ===========================================

export function OffBalanceSheetDialog({
  open,
  onOpenChange,
  items: initialItems,
  onSave,
}: OffBalanceSheetDialogProps) {
  const [items, setItems] = useState<OffBalanceSheetItem[]>(
    initialItems.length > 0 ? initialItems : []
  );

  const handleAddItem = (suggested?: { name: string; description?: string }) => {
    const newItem: OffBalanceSheetItem = {
      id: generateId(),
      name: suggested?.name || '',
      value: '',
      description: suggested?.description,
    };
    setItems((prev) => [...prev, newItem]);
  };

  const handleUpdateItem = (id: string, updatedItem: OffBalanceSheetItem) => {
    setItems((prev) => 
      prev.map((item) => (item.id === id ? updatedItem : item))
    );
  };

  const handleDeleteItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSave = () => {
    // Filter out empty items
    const validItems = items.filter(
      (item) => item.name.trim() !== '' && item.value.trim() !== ''
    );
    onSave(validItems);
    onOpenChange(false);
  };

  const handleClose = () => {
    setItems(initialItems);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Off-Balance Sheet Records
          </DialogTitle>
          <DialogDescription>
            Add off-balance sheet items to include in your financial analysis and custom ratio calculations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Quick Add Suggestions */}
          <div>
            <Label className="mb-2 block">Quick Add</Label>
            <div className="flex flex-wrap gap-2">
              {suggestedItems.map((suggested) => (
                <Button
                  key={suggested.name}
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddItem(suggested)}
                  disabled={items.some((i) => i.name === suggested.name)}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {suggested.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Items List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label>Items ({items.length})</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddItem()}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Custom Item
              </Button>
            </div>

            {items.length === 0 ? (
              <div className="text-center py-8 border rounded-lg bg-muted/30">
                <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No off-balance sheet items added yet.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Use the quick add buttons above or click "Add Custom Item"
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[250px] pr-4">
                <div className="space-y-2">
                  {items.map((item) => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      onChange={(updated) => handleUpdateItem(item.id, updated)}
                      onDelete={() => handleDeleteItem(item.id)}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Save Items ({items.filter((i) => i.name && i.value).length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

